import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Dialog } from "@akuna/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { OptionTile } from "@/components/packages/plan-card/OptionTile";
import { FixedWeekdaysField } from "@/pages/daycare-hotel/plans/FixedWeekdaysField";
import { cancelStayPlanContract } from "@/pages/daycare-hotel/plans/useCancelStayPlan";
import { switchStayPlan } from "@/hooks/useStayPlanContractConfig";
import { useActiveStayPlanContracts, type ActiveStayPlanContract } from "@/hooks/useActiveStayPlanContracts";
import { useDeleteStayPlan, useStayPlans, type StayPlan } from "@/hooks/useStayPlans";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";

type Resolution = "remove" | "switch";
type FailureReason = "permission" | "notFound" | "alreadyResolved" | "activePlan" | "unknown";

function getFailureReason(error: unknown): FailureReason {
  const message = (error as { message?: string } | null)?.message ?? "";
  if (message === "permission_denied") return "permission";
  if (message === "plan_not_found") return "notFound";
  if (message === "contract_not_found" || message === "contract_cancelled") return "alreadyResolved";
  if (message === "pet_already_has_active_matricula") return "activePlan";
  return "unknown";
}

export interface DeleteStayPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: StayPlanModule;
  plan: StayPlan;
}

export default function DeleteStayPlanDialog({ open, onOpenChange, module, plan }: DeleteStayPlanDialogProps) {
  const { t } = useTranslation("daycareHotel");
  const ns = plan.kind === "day_package" ? "packages" : "plans";
  const queryClient = useQueryClient();
  const contracts = useActiveStayPlanContracts(module, plan.id, open);
  const catalogQuery = useStayPlans(module);
  const deletePlan = useDeleteStayPlan(module);

  const [resolution, setResolution] = useState<Resolution>("remove");
  const [targetPlanId, setTargetPlanId] = useState("");
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [pending, setPending] = useState<ActiveStayPlanContract[] | null>(null);
  const [failed, setFailed] = useState<Array<{ contract: ActiveStayPlanContract; reason: FailureReason }>>([]);
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    if (open) return;
    setResolution("remove");
    setTargetPlanId("");
    setWeekdays([]);
    setPending(null);
    setFailed([]);
    setIsResolving(false);
  }, [open]);

  const activePets = pending ?? contracts.data ?? [];
  const isLoading = contracts.isLoading;
  const hasActivePets = !isLoading && activePets.length > 0;
  const showSimpleConfirm = !isLoading && activePets.length === 0;

  const catalog = (catalogQuery.data ?? []).filter(
    (candidate) => candidate.kind === "recurring" && candidate.isActive && candidate.id !== plan.id,
  );
  const targetPlan = catalog.find((candidate) => candidate.id === targetPlanId) ?? null;
  const needsWeekdays = module === "daycare" && targetPlan?.usageRule === "per_week";
  const requiredWeekdays = needsWeekdays ? targetPlan?.usageLimit ?? 0 : 0;
  const weekdaysReady = !needsWeekdays || weekdays.length === requiredWeekdays;
  const canContinue = resolution === "remove" || (Boolean(targetPlanId) && weekdaysReady);

  const finishDelete = async () => {
    try {
      await deletePlan.mutateAsync({ id: plan.id });
      toast({ title: t(`${ns}.deleted`) });
      onOpenChange(false);
    } catch {
      toast({ variant: "destructive", title: t(`${ns}.deleteFailed`) });
    }
  };

  const runResolution = async () => {
    setIsResolving(true);
    const toProcess = pending ?? contracts.data ?? [];
    const stillPending: ActiveStayPlanContract[] = [];
    const newFailed: Array<{ contract: ActiveStayPlanContract; reason: FailureReason }> = [];

    for (const contract of toProcess) {
      try {
        if (resolution === "remove") {
          await cancelStayPlanContract({ module, contractId: contract.contractId });
        } else {
          await switchStayPlan({
            module,
            contractId: contract.contractId,
            newPlanId: targetPlanId,
            usageRule: plan.usageRule,
            fixedWeekdays: weekdays,
          });
        }
      } catch (error) {
        stillPending.push(contract);
        newFailed.push({ contract, reason: getFailureReason(error) });
      }
    }

    setPending(stillPending);
    setFailed(newFailed);
    setIsResolving(false);

    if (stillPending.length === 0) {
      queryClient.invalidateQueries({ queryKey: ["stay-plan-rows"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
      await finishDelete();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      mode="adaptive"
      className="sm:max-w-[520px]"
      title={t(`${ns}.deleteTitle`, { name: plan.name })}
      closeLabel={t(`${ns}.deleteCancel`)}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isResolving || deletePlan.isPending}
          >
            {t(`${ns}.deleteCancel`)}
          </Button>
          {showSimpleConfirm ? (
            <Button type="button" variant="destructive" onClick={() => void finishDelete()} loading={deletePlan.isPending}>
              {t(`${ns}.deleteConfirm`)}
            </Button>
          ) : null}
          {hasActivePets ? (
            <Button
              type="button"
              onClick={() => void runResolution()}
              disabled={!canContinue}
              loading={isResolving || deletePlan.isPending}
            >
              {failed.length > 0 ? t(`${ns}.resolveRetryFailed`) : t(`${ns}.resolveContinue`)}
            </Button>
          ) : null}
        </div>
      }
    >
      {isLoading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : showSimpleConfirm ? (
        <p className="text-sm text-muted-foreground">{t(`${ns}.deleteNoContractsBody`)}</p>
      ) : (
        <div className="grid gap-4">
          <p className="text-sm text-muted-foreground">
            {t(`${ns}.deleteHasContractsBody`, { count: activePets.length })}
          </p>
          <ul className="max-h-40 overflow-y-auto rounded-md border border-border">
            {activePets.map((contract) => (
              <li
                key={contract.contractId}
                className="flex items-center justify-between border-b border-border px-3 py-2 text-sm last:border-b-0"
              >
                <span>{contract.petName}</span>
                <span className="text-muted-foreground">{contract.ownerName}</span>
              </li>
            ))}
          </ul>

          {failed.length > 0 ? (
            <div role="alert" className="space-y-1 text-xs text-state-danger-foreground">
              <p>{t(`${ns}.resolveFailedPets`, { count: failed.length })}</p>
              <ul className="list-disc space-y-1 pl-4">
                {failed.map(({ contract, reason }) => (
                  <li key={contract.contractId}>
                    {contract.petName}: {t(`${ns}.resolveFailureReasons.${reason}`)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="grid gap-2.5 sm:grid-cols-2">
            <OptionTile
              name="delete-resolution"
              value="remove"
              checked={resolution === "remove"}
              disabled={isResolving}
              onSelect={() => setResolution("remove")}
            >
              <span className="block text-[0.8125rem] font-semibold text-foreground">{t(`${ns}.resolveRemove`)}</span>
              <span className="mt-1 block text-xs text-muted-foreground">{t(`${ns}.resolveRemoveHint`)}</span>
            </OptionTile>
            <OptionTile
              name="delete-resolution"
              value="switch"
              checked={resolution === "switch"}
              disabled={isResolving}
              onSelect={() => setResolution("switch")}
            >
              <span className="block text-[0.8125rem] font-semibold text-foreground">{t(`${ns}.resolveSwitch`)}</span>
              <span className="mt-1 block text-xs text-muted-foreground">{t(`${ns}.resolveSwitchHint`)}</span>
            </OptionTile>
          </div>

          {resolution === "switch" ? (
            <div className="grid gap-3">
              <Select value={targetPlanId} onValueChange={setTargetPlanId} disabled={isResolving}>
                <SelectTrigger aria-label={t(`${ns}.resolveSwitch`)}>
                  <SelectValue placeholder={t(`${ns}.resolveSwitchPlaceholder`)} />
                </SelectTrigger>
                <SelectContent>
                  {catalog.map((candidate) => (
                    <SelectItem key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {needsWeekdays ? (
                <FixedWeekdaysField
                  value={weekdays}
                  required={requiredWeekdays}
                  onChange={setWeekdays}
                  disabled={isResolving}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
